<template>
  <div>
    <a-form
      class="form leader-project-form"
      layout="vertical"
      :model="model"
      :rules="rules"
      @validate="errors.onValidate"
      @finish="onFinish"
    >
      <leader-project-form-row
        :show-available="showAvailability"
        :offsetAvailable="false"
        :available="model.availabilityList['scientificScopes']"
        :disabled="disabled"
        @change="changeAvailability('scientificScopes')"
      >
        <a-form-item>
          <h3>Шаг 8. Научные направления</h3>
        </a-form-item>
      </leader-project-form-row>

      <a-form-item
        name="scientificScopes"
        :help="errors.getError('scientificScopes')"
        :validate-status="errors.getStatus('scientificScopes')"
      >
        <c-input-udc-list
          :items="model.scientificScopes"
          :project-id="route.params.id"
          :disabled="disabled"
          :show-selector="false"
          @delete="onUdcDelete"
        >
          <template #selected-header
            ><snap class="udc-selection-header">Вы выбрали:</snap></template
          >
        </c-input-udc-list>
      </a-form-item>

      <a-divider />

      <p>Выберите, к каким научным направлениям относится проект.</p>

      <a-flex class="scopes">
        <project-udc-item
          v-for="item in mainUdc"
          :key="'udc-' + item.notation"
          :item="item"
          :delete-udc-id="udcIdToDelete"
          :disabled="disabled"
          @update="handleUdcUpdate"
        />
      </a-flex>

      <leader-project-form-row :show-available="false">
        <leader-project-form-buttons
          v-if="project?.isMyProject"
          :project-status-id="project?.projectStatusId"
          :loading="isLoading"
          @click="setGoNext"
          @confirm-update="() => emit('confirm-update')"
        />
      </leader-project-form-row>
    </a-form>
  </div>
</template>

<script setup>
  import { computed, onMounted, reactive, ref } from 'vue';
  import { useApi } from '@/api/use-api';
  import LeaderProjectFormRow from '@/views/leader/projects/form/LeaderProjectFormRow.vue';
  import ErrorsHandler from '@/components/form/errors-handler';
  import rules from './rules/RulesStep7';
  import { useRoute, useRouter } from 'vue-router';
  import LeaderProjectFormButtons from '@/views/leader/projects/form/LeaderProjectFormButtons.vue';
  import ProjectUdcItem from '@/components/projects/components/ProjectUdcItem.vue';
  import CInputUdcList from '@/components/UI/CInputUdcList.vue';
  import ScientificScopesAdapter from '@/api/udc/scientific-scopes-adapter';

  const router = useRouter();
  const route = useRoute();
  const api = useApi();
  const emit = defineEmits(['update', 'confirm-update', 'update-type']);
  const mainUdc = ref([]);
  const isLoading = ref(false);
  const udcIdToDelete = ref(null);

  const props = defineProps({
    project: {
      type: Object,
      require: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  });

  const model = reactive({
    id: null,
    isExternal: false,
    udcStepPassed: false,
    availabilityList: [],
    scientificScopes: [],
  });

  const errors = new ErrorsHandler(
    {
      scientificScopes: '',
    },
    (fieldName) => {
      if ('udcIdList' === fieldName) {
        return 'scientificScopes';
      }
      return fieldName;
    }
  );

  const showAvailability = computed(() => {
    return model.isExternal || !!model.partnerId;
  });

  onMounted(async () => {
    fillModel();
    await getMainUdc();
  });

  const fillModel = () => {
    try {
      model.id = props.project.id;
      model.isExternal = props.project.isExternal;
      model.udcStepPassed = props.project.udcStepPassed;
      model.availabilityList = props.project.availabilityList;
      model.scientificScopes = new ScientificScopesAdapter().convertMany(
        props.project.scientificScopes
      );
    } catch (error) {
      console.error('error', error);
    }
  };

  const goNext = ref(false);
  const setGoNext = (value) => {
    goNext.value = !!value;
  };

  const getMainUdc = async () => {
    try {
      // Для получения главных УДК в поиске topUdcId = null (не указываем в параметрах)
      const response = await api.udc.getDescendants(
        new URLSearchParams({
          projectId: route.params.id,
        })
      );
      mainUdc.value = response.udcList.items;
    } catch (error) {
      console.error('error', error);
    }
  };

  const handleUdcUpdate = (update) => {
    udcIdToDelete.value = null;
    const udc = new ScientificScopesAdapter().convertOne(update.udc);
    const isChecked = update.isChecked;
    if (isChecked) {
      const foundScope = model.scientificScopes.find((el) => el === udc.id);
      if (!foundScope) {
        model.scientificScopes.push(udc);
      }
    } else {
      model.scientificScopes = model.scientificScopes.filter(
        (el) => el.id !== udc.id
      );
    }
  };

  const onUdcDelete = (udcClassId) => {
    model.scientificScopes = model.scientificScopes.filter(
      (el) => el.id !== udcClassId
    );
    udcIdToDelete.value = udcClassId;
  };

  const changeAvailability = (key) => {
    model.availabilityList[key] = !model.availabilityList[key];
  };

  const onFinish = async () => {
    isLoading.value = true;
    try {
      await api.projectUdc.saveUdcIdList(route.params.id, {
        udcIdList: getUdcIdList(model.scientificScopes),
      });

      await api.leaderPage.updateProject({
        id: route.params.id,
        udcStepPassed: true,
        udcIdList: getUdcIdList(model.scientificScopes),
        isScientificScopesAvailable: model.availabilityList['scientificScopes'],
      });

      emit('update', model);
      if (goNext.value) {
        await router.push({ hash: '#9' });
      }
    } catch (error) {
      errors.handleApiErrors(error);
    } finally {
      isLoading.value = false;
    }
  };
  defineExpose({ onFinish });

  const getUdcIdList = (scientificScopes) => {
    return scientificScopes.map((el) => el.id);
  };
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;

  .ant-form-item {
    margin-bottom: 0;
  }

  :deep(.ant-input) {
    font-family: 'Nunito Sans', sans-serif;

    &[disabled] {
      color: $color-text-primary;
    }
  }

  :deep(.leader-project-form__available) {
    padding-top: 8px;
  }

  .scopes {
    flex-direction: column;
    row-gap: 8px;
    margin-bottom: 50px;
  }

  .udc-selection-header {
    font-family: Nunito Sans, sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 20px;
    vertical-align: middle;
    margin-bottom: 16px;
  }
</style>
